<template>
  <v-card class="py-6 px-8">
    <TemplateLabel v-if="isTemplate" />
    <v-card-title class="pa-0 ma-0 h6 blue-grey--text text--darken-3">
      {{ $t('routines.routine_summary') }}
    </v-card-title>
    <v-divider class="my-6" />
    <v-card-text class="ma-0 pa-0 d-flex flex-column">
      <div class="d-flex justify-space-between">
        <div class="d-flex flex-column me-10">
          <p
            class="ma-0 pa-0 blue-grey--text text--darken-3 subtitle-2 text-wrap-anywhere"
          >
            {{ routine.name }}
          </p>

          <p
            class="blue-grey--text text--darken-1 body-2 pa-0 ma-0 text-wrap-anywhere"
            style="white-space: pre-line"
          >
            {{ routine.description }}
          </p>
        </div>
        <div class="d-flex flex-column" style="min-width: 280px">
          <ChipAttributes
            class="mb-4"
            v-if="routine.frequency"
            :label="$t('routines.frequency_label')"
            :value="[{ text: $t(`routines.frequency.${routine.frequency}`) }]"
          />
          <ChipAttributes
            class="mb-4"
            v-if="routine.type"
            :label="$t('common.type')"
            :value="[{ text: $t(`routines.type.${routine.type}`) }]"
          />
          <ChipAttributes
            class="mb-4"
            v-if="showSituationTypes"
            :label="$t('settings.situation_types')"
            :value="routineSituationTypes"
          />
          <ChipAttributes
            class="mb-4"
            v-if="showCategories"
            :label="$t('common.category_other')"
            :value="routineCategories"
          />
          <ChipAttributes
            class="mb-4"
            v-if="routinePresetTags.length"
            :label="$t('common.attribute_other')"
            :value="routinePresetTags"
          />
          <ChipAttributes
            :label="$t('routines.created_in')"
            :value="[{ text: location.site.name }]"
          />
        </div>
      </div>
      <div v-if="collaborators.length" class="mt-4">
        <p class="caption blue-grey--text text--darken-1 ma-0 pa-0 mb-2">
          {{ $t('common.collaborator_other') }}
        </p>
        <AssigneesSelection
          disabled
          :accounts="collaborators"
          :maxItemsToShow="10"
        />
      </div>
      <v-divider class="my-6" />
      <p class="subtitle-2 blue-grey--text text--darken-3 ma-0 pa-0 mb-4">
        {{ $t('common.assignee_other') }}
      </p>
      <div v-if="routine.isGloballyAssigned">
        <v-chip class="routine-global-chip">
          <v-icon size="24" color="blue" class="me-1">mdi-earth </v-icon>
          <p class="ma-0 pa-0 blue--text body-2">
            {{ $t('routines.extent_card_global_title') }}
          </p>
        </v-chip>
      </div>

      <div v-else>
        <ChipAttributes
          v-if="assignedSites.length"
          class="mb-4"
          :label="$t('common.site_other')"
          :value="assignedSites"
        />
        <ChipAttributes
          v-if="assignedTeams.length"
          class="mb-4"
          :label="$t('common.team_other')"
          :value="assignedTeams"
        />
        <div v-if="assignees.length">
          <p class="caption blue-grey--text text--darken-1 ma-0 pa-0 mb-2">
            {{ $t('common.users.user_other') }}
          </p>
          <AssigneesSelection
            disabled
            :accounts="assignees"
            :maxItemsToShow="10"
          />
        </div>
      </div>
      <v-divider
        v-if="showChecklistPreview"
        class="my-6"
        data-testid="routineConfirmation-checklist"
      />
      <p
        v-if="showChecklistPreview"
        class="subtitle-2 blue-grey--text text--darken-3 ma-0 pa-0 mb-4"
        data-testid="routineConfirmation-title-checklistAndScoring"
      >
        {{
          hasScoringConfiguration
            ? $t('routines.checklist_and_scoring')
            : $t('checklist.checklist_one')
        }}
      </p>
      <v-row v-if="showChecklistPreview" class="ma-0">
        <v-col
          cols="12"
          :md="hasScoringConfiguration ? 6 : 12"
          class="pa-0"
          :data-testid="
            hasScoringConfiguration
              ? 'routineConfirmation-checklistColumn-half'
              : 'routineConfirmation-checklistColumn-full'
          "
        >
          <ChecklistOverview
            data-testid="routineConfirmation-checklistOverview-preview"
            :checklist="checklist"
            :hideTitle="true"
          />
        </v-col>

        <v-col
          v-if="hasScoringConfiguration"
          cols="12"
          md="6"
          class="pa-0 ps-md-6 pt-4 pt-md-0"
          data-testid="routineConfirmation-scoringColumn-half"
        >
          <aside data-testid="routineConfirmation-scoringSummary">
            <div
              class="confirmation-scoring-summary__row mb-4"
              data-testid="routineConfirmation-row-scoringType"
            >
              <p
                class="caption blue-grey--text text--darken-1 ma-0 pa-0 mb-1"
                data-testid="routineConfirmation-label-scoringType"
              >
                {{ $t('routines.scoring.scoring_type') }}
              </p>
              <p
                class="body-2 blue-grey--text text--darken-3 ma-0 pa-0"
                data-testid="routineConfirmation-value-scoringType"
              >
                <span class="font-weight-medium"
                  >{{ scoringMode.title }}:&nbsp;</span
                >
                <span class="blue-grey--text text--darken-1 body-2">{{
                  scoringMode.description
                }}</span>
              </p>
            </div>

            <p
              v-if="hasItemsWithVisibilityRules"
              data-testid="routineConfirmation-text-maxPointsUnavailable"
              class="body-2 blue-grey--text text--darken-1 ma-0 pa-0"
            >
              {{ $t('routines.scoring.max_points_unavailable') }}
            </p>

            <div
              v-else
              class="confirmation-scoring-summary__row"
              data-testid="routineConfirmation-row-maxPoints"
            >
              <p
                class="caption blue-grey--text text--darken-1 ma-0 pa-0 mb-1"
                data-testid="routineConfirmation-label-maxPoints"
              >
                {{ $t('routines.scoring.max_points') }}
              </p>
              <p
                class="body-2 blue-grey--text text--darken-3 font-weight-medium ma-0 pa-0"
                data-testid="routineConfirmation-value-maxPoints"
              >
                {{ localizedMaxPoints }}
              </p>
            </div>
          </aside>
        </v-col>
      </v-row>
    </v-card-text>

    <v-card-actions
      class="justify-end ma-0 pa-0 pt-4 pb-3 mt-8 buttons-container"
    >
      <Button
        class="me-2"
        color="grey"
        data-testid="routineConfirmation-button-back"
        icon="mdi-chevron-left"
        plain
        :disabled="isSaving"
        :text="$t('common.button.back')"
        @click="$emit('back')"
      />

      <Button
        :text="$t('common.button.create')"
        color="primary"
        type="submit"
        data-testid="routineConfirmation-button-create"
        :disabled="isSaving || disableConfirm"
        :loading="isSaving"
        @click="$emit('confirm')"
      />
    </v-card-actions>
  </v-card>
</template>

<script>
import _ from 'lodash'
import { mapGetters } from 'vuex'
import { utils } from 'frontend-js-shared'

import userMixin from '@/mixins/userMixin.vue'
import mixin from '@/mixins/mixin.vue'
import api from '@/api'

import Button from '@/components/UI/Button.vue'
import TemplateLabel from '@/components/TemplateLabel.vue'
import ChecklistOverview from '@/components/Checklists/ChecklistOverview.vue'
import AssigneesSelection from '@/components/AssigneesSelector/AssigneesSelection.vue'
import { formatLocalizedPoints } from '@/lib/formatLocalizedPoints'

import ChipAttributes from './ChipAttributes.vue'
import { calculateChecklistMaxScore } from './scoring/scoringViewModel'

export default {
  name: 'ConfirmationStep',

  mixins: [mixin, userMixin],

  props: {
    isSaving: { required: false, type: Boolean },
    checklist: { required: false, type: Object },
    routine: { required: true, type: Object },
    location: { required: false, type: Object },
    isTemplate: {
      type: Boolean,
    },
    showChecklistPreview: {
      type: Boolean,
    },
    disableConfirm: {
      type: Boolean,
    },
  },

  components: {
    TemplateLabel,
    ChecklistOverview,
    ChipAttributes,
    AssigneesSelection,
    Button,
  },

  data() {
    return {
      assignees: [],
      collaborators: [],
      assignedTeams: [],
    }
  },

  mounted() {
    if (this.accounts?.length) this.getAccounts()
    if (this.routine?.assignedTeams?.length) this.fetchTeams()
  },

  computed: {
    ...mapGetters(['organizationId', 'organizationTags', 'organizationSites']),

    routineSituationTypes() {
      return this.routine.presetSituationTypes.map(st => {
        return {
          ...st,
          text: this.$t(
            `quick_action.situation_types.situations.${st.situation}`
          ),
        }
      })
    },

    routineCategories() {
      return this.routine.categories.map(category => {
        return {
          ...category,
          text: this.$t(`common.categories.${category}`),
        }
      })
    },

    routinePresetTags() {
      return this.fullPresetTags.map(tag => {
        return {
          ...tag,
          text: tag.name,
        }
      })
    },

    showCategories() {
      return Boolean(this.routine?.categories?.length)
    },

    showSituationTypes() {
      return Boolean(this.routine?.presetSituationTypes?.length)
    },

    assignedSites() {
      if (this.routine?.assignedSites?.length === 0) return []

      const assignedSites = this.organizationSites?.filter?.(site => {
        return this.routine?.assignedSites?.includes(site.id) ?? []
      })

      return assignedSites.map(site => {
        return { ...site, text: site.name }
      })
    },

    fullPresetTags() {
      return this.organizationTags?.filter?.(tag => {
        return this.routine?.presetTags?.includes(tag.id) ?? []
      })
    },

    accounts() {
      return [
        ...new Set([
          ...(this.routine?.assignees ?? []),
          ...(this.routine?.collaborators ?? []),
        ]),
      ]
    },

    hasScoringConfiguration() {
      return Boolean(this.checklist?.scoringType && this.scoringMode)
    },

    scoringMode() {
      if (this.checklist?.scoringType === 'question') {
        return {
          title: this.$t('routines.scoring.mode_question_title'),
          description: this.$t('routines.scoring.mode_question_description'),
        }
      }

      if (this.checklist?.scoringType === 'section') {
        return {
          title: this.$t('routines.scoring.mode_section_title'),
          description: this.$t('routines.scoring.mode_section_description'),
        }
      }

      return null
    },

    maxPoints() {
      return calculateChecklistMaxScore(this.checklist?.items || [])
    },

    localizedMaxPoints() {
      return formatLocalizedPoints(this.maxPoints)
    },

    hasItemsWithVisibilityRules() {
      return (this.checklist?.items || []).some(item =>
        utils.checklists.hasVisibilityRule(item)
      )
    },
  },

  methods: {
    async getAccounts() {
      if (!this.accounts?.length) return

      const response = await api.account.getAccountsByIds(this.organizationId, {
        filters: { id: this.accounts },
        limit: this.accounts?.length ?? 0,
      })

      this.assignees = response.data.results.filter(assignee =>
        this.routine.assignees.includes(assignee.id)
      )

      if (this.routine?.collaborators.length > 0) {
        this.collaborators = response.data.results.filter(collaborator =>
          this.routine.collaborators.includes(collaborator.id)
        )
      }
    },

    async fetchTeams() {
      try {
        if (_.isEmpty(this.routines?.assignedTeams)) return []
        const response = await api.teams.getTeamsStream(this.organizationId, {
          filters: { ids: this.routine.assignedTeams },
          from: 0,
          sort: { by: 'name', order: 'asc' },
        })

        this.assignedTeams = response?.data?.results.map(result => {
          return { ...result.team, text: result.team.name }
        })
      } catch (err) {
        this.handleRequestError(err)
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.confirmation-scoring-summary__row {
  word-break: break-word;
}

.buttons-container {
  position: sticky;
  bottom: 0;
  background: linear-gradient(to bottom, transparent 10%, white 100%);
}
</style>

<style lang="scss">
.routine-global-chip {
  border: 1px solid $color-blue-lighten1 !important;
  background-color: $color-blue-lighten2 !important;
}
</style>
